import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import customAxios from '../../../../../api/customAxios';
import { authHeader } from '../../../../../authentication/authTokenService';
import { 
  Box, 
  Typography, 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  FormControlLabel,
  RadioGroup,
  Radio,
  Card,
  CardContent,
  CardHeader,
  Grid,
  CircularProgress,
  IconButton,
  Avatar,
  InputAdornment,
  Container,
  Stack,
  Tooltip,
  FormHelperText,
  FormLabel,
  Autocomplete,
  Alert
} from '@mui/material';
import {
  Close as X,
  AttachMoney as DollarSign,
  Info,
  AutoFixHigh as Wand2
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { pink } from '@mui/material/colors';
import { tooltipClasses } from "@mui/material/Tooltip";

// Import our modular components
import { getAllElementOptions } from './ElementTypeHierarchy';
import { PhotoDocumentationSection } from './PhotoDocumentation';
import DefectDatabaseSearch from './DefectDatabaseSearch';
import { fetchSubCategoryElementList } from '../../../api/elementSectionApi';
import { resetSliceState } from '../element-section/elementSectionSlice';

// Import backend integration helpers from old form
import { 
  calulateTotal, // Use old form's calculation logic
  convertToDecimal, // For threshold warning calculation
  setDefaultValues,
  setDefaultRiskTypeValues
} from '../defectHelper';

// Import constants and utilities from old form
import { riskTypeOptions } from '../../../../../utils/constants';
import {
  riskOptions,
  priorityOptions,
  defectTypes,
  others,
  getUnits
} from '../../../../../common/dropdowns';
import {
  getLabel,
  getTranslationVariantText,
  getIntlTranslation,
} from '../../../../../utils/helper';

import enTranslations from '../../../../../lang/en';

// Styled tooltip to match main defect mask
const StyledTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#fff',
    color: '#000',
    maxWidth: 500,
    border: '1px solid #dadde9',
    padding: '16px',
    fontSize: '14px',
    lineHeight: '1.5',
    boxShadow: theme.shadows[1]
  },
}));

// Radio button styling to match main defect mask exactly
const radioButton = {
  "&.Mui-checked": {
    color: pink[600],
  },
};

// Highlighted text component for tooltips
const HighlightedText = ({ color, children }) => (
  <span style={{ color, fontWeight: 'bold' }}>{children}</span>
);

// Warning content component for risk tooltips
const WarningContent = ({ type }) => {
  if (type === "high") {
    return (
      <div>
        {getTranslationVariantText("high-warning-start", "subtitle")}{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("health", "subtitle")}</HighlightedText> {getTranslationVariantText("and-or", "subtitle")}{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("safety", "subtitle")}</HighlightedText>, {getTranslationVariantText("including", "subtitle")}{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("structural-safety", "subtitle")}</HighlightedText>,{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("fire-protection", "subtitle")}</HighlightedText> {getTranslationVariantText("and", "subtitle")}{' '}
        <HighlightedText color="#ff0000">{getTranslationVariantText("building-laws", "subtitle")}</HighlightedText> /{' '}
        <HighlightedText color="#000">{getTranslationVariantText("high-warning-end", "subtitle")}</HighlightedText>
      </div>
    );
  } else if (type === "medium") {
    return (
      <div>
        {getTranslationVariantText("medium-warning-start", "subtitle")}{' '}
        <HighlightedText color="#ffa500">{getTranslationVariantText("functionality-usability", "subtitle")}</HighlightedText>{' '}
        {getTranslationVariantText("is", "subtitle")}{' '}
        <HighlightedText color="#ffa500">{getTranslationVariantText("at-risk", "subtitle")}</HighlightedText>, {getTranslationVariantText("as-well-as", "subtitle")}{' '}
        <HighlightedText color="#ffa500">{getTranslationVariantText("overdue-lifecycle", "subtitle")}</HighlightedText>{' '}
        <HighlightedText color="#000">{getTranslationVariantText("medium-warning-end", "subtitle")}</HighlightedText>
      </div>
    );
  } else {
    return (
      <div>
        <HighlightedText color="#008000">{getTranslationVariantText("defects-problems", "subtitle")}</HighlightedText>{' '}
        <HighlightedText color="#000">{getTranslationVariantText("low-warning-middle", "subtitle")}</HighlightedText>{' '}
        <HighlightedText color="#008000">{getTranslationVariantText("future-maintenance", "subtitle")}</HighlightedText>,{' '}
        <HighlightedText color="#000">{getTranslationVariantText("low-warning-end", "subtitle")}</HighlightedText>
      </div>
    );
  }
};

// Risk tooltip content using actual translation text
const getRiskTooltipContent = (riskLevel) => {
  switch (riskLevel.toLowerCase()) {
    case 'high':
      return (
        <Box sx={{ maxWidth: 400 }}>
          <Typography variant="body2">
            {enTranslations["high-warning-start"]} <strong>{enTranslations.health}</strong> {enTranslations["and-or"]} <strong>{enTranslations.safety}</strong>, {enTranslations.including} <strong>{enTranslations["structural-safety"]}</strong>, <strong>{enTranslations["fire-protection"]}</strong> {enTranslations["and-or"]} <strong>{enTranslations["building-laws"]}</strong> / {enTranslations["high-warning-end"]}
          </Typography>
        </Box>
      );
    case 'medium':
      return (
        <Box sx={{ maxWidth: 400 }}>
          <Typography variant="body2">
            {enTranslations["medium-warning-start"]} <strong>{enTranslations["functionality-usability"]}</strong> {enTranslations.is} {enTranslations["at-risk"]}, {enTranslations["as-well-as"]} <strong>{enTranslations["overdue-lifecycle"]}</strong> {enTranslations["medium-warning-end"]}
          </Typography>
        </Box>
      );
    case 'low':
      return (
        <Box sx={{ maxWidth: 400 }}>
          <Typography variant="body2">
            <strong>{enTranslations["defects-problems"]}</strong> {enTranslations["low-warning-middle"]} <strong>{enTranslations["future-maintenance"]}</strong> {enTranslations["low-warning-end"]}
          </Typography>
        </Box>
      );
    default:
      return <Typography variant="body2">Select a risk level for guidance.</Typography>;
  }
};

function EnhancedDefectForm({ values, setFieldValue, errors, touched, subCategory, defectsData = null }) {
  // Get category and subcategory from props (passed from parent CreateDefect component)
  const selectedCategory = subCategory?.parent?.name || 'Technical Building Equipment';
  const selectedSubCategory = subCategory?.name || 'WasteWater & Water Gas';

  // Import for element fetching - moved before getTextLabel to fix initialization error
  const scope = useSelector((state) => state.container.scope);
  const elementState = useSelector((state) => state.tddElementSection);
  const dispatch = useDispatch();

  // Component cleanup on mount to ensure fresh state
  useEffect(() => {
    console.log('EnhancedDefectForm mounted - clearing any cached state');
    
    // Clear any element section state that might be cached
    dispatch(resetSliceState());
    
    // Clear any local state that might cause issues
    setFilteredElementOptions([]);
    setIsElementOther(false);
    
    // Clear any defects database cache that might cause issues
    try {
      // Import defectsLazyLoader if available and clear its cache
      import('../../../../../utils/defectsLazyLoader').then(module => {
        const defectsLazyLoader = module.default;
        if (defectsLazyLoader && typeof defectsLazyLoader.clearCache === 'function') {
          defectsLazyLoader.clearCache();
        }
      }).catch(err => {
        console.log('No defectsLazyLoader to clear:', err.message);
      });
    } catch (error) {
      console.log('Error clearing defects cache:', error.message);
    }
    
    return () => {
      console.log('EnhancedDefectForm unmounting - cleaning up state');
      // Additional cleanup if needed
    };
  }, [dispatch]);

  // Helper function to get text-only translations for form labels (like old form)
  const getTextLabel = useCallback((key, fallback = '') => {
    try {
      // For cost data and other key labels, use getIntlTranslation like the old form
      const intlTranslationKeys = [
        '_Cost-Data', '_Unit', '_Price-per-Unit', '_Quantity', 
        '_Extra-Cost-(Optional)', '_%-of-the-element-affected', '_Extra-Cost-Comments',
        '_quelle', '_Q&A'
      ];
      
      if (intlTranslationKeys.includes(key)) {
        const result = getIntlTranslation(key);
        // Ensure we always return a string
        return typeof result === 'string' ? result : (fallback || key.replace(/^_/, '').replace(/-/g, ' '));
      }
      
      // Fallback to cleaned key for any remaining cases
      const cleanedKey = key.replace(/^_/, '').replace(/-/g, ' ');
      return fallback || cleanedKey;
    } catch (error) {
      const cleanedKey = key.replace(/^_/, '').replace(/-/g, ' ');
      return fallback || cleanedKey;
    }
  }, []);

  // Safe translation function that always returns a string
  const getSafeTranslation = useCallback((key, fallback = '') => {
    try {
      const result = getIntlTranslation(key);
      return typeof result === 'string' ? result : (fallback || key.replace(/^_/, '').replace(/-/g, ' '));
    } catch (error) {
      return fallback || key.replace(/^_/, '').replace(/-/g, ' ');
    }
  }, []);

  // Radio button options - same as old form
  const riskTypeOptionsArray = riskOptions;
  const defectTypeOptions = defectTypes;
  const priorityTypeOptions = !values.costDefect
    ? priorityOptions.filter((f) => f.nonCostDefect)
    : priorityOptions;

  // State management
  const [aiParaphrasingDefect, setAiParaphrasingDefect] = useState(false);
  const [aiParaphrasingMeasure, setAiParaphrasingMeasure] = useState(false);
  const [showPriorityDistribution, setShowPriorityDistribution] = useState(false);
  const [filteredElementOptions, setFilteredElementOptions] = useState([]);
  const [isElementOther, setIsElementOther] = useState(false);
  const fileInputRef = useRef(null);

  // Helper function to safely convert values to strings  
  const safeStringValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return '';
    return String(value);
  };

  // Helper function to update Formik values (optimized for backend compatibility)
  const setFormData = useCallback((updates) => {
    Object.keys(updates).forEach(key => {
      if (key === 'isNonCostDefect') {
        setFieldValue('costDefect', !updates[key]);
      } else if (key === 'inRedFlag') {
        setFieldValue('inRedFlag', updates[key]);
      } else if (key === 'costComment') {
        // Map costComment to both API fields for compatibility
        setFieldValue('extraCostComment', updates[key]);
        setFieldValue('costComment', updates[key]);
      } else if (key === 'priorityType') {
        // Sync priority fields for backend compatibility
        setFieldValue('priorityType', updates[key]);
        setFieldValue('priority', updates[key]);
      } else if (key === 'priority') {
        // Sync priority fields for backend compatibility
        setFieldValue('priority', updates[key]);
        setFieldValue('priorityType', updates[key]);
      } else {
        setFieldValue(key, updates[key]);
      }
    });
  }, [setFieldValue]);

  // Optimized change handlers with backend integration (exactly like old form)
  const handleRiskChange = useCallback((value) => {
    setFieldValue('riskType', value);
    
    // Backend integration: Apply default values based on risk type (like old form)
    const defaults = setDefaultRiskTypeValues(value);
    
    // Enhanced safety check for low risk selection (exactly like old form)
    if (value === riskTypeOptions.LOW) {
      // First check if red flag is checked
      if (values.inRedFlag) {
        // Uncheck it before disabling
        setFieldValue('inRedFlag', false);
      }
    } else {
      // For other risk types, use the default behavior
      setFieldValue('inRedFlag', defaults.isRedFlag);
    }
  }, [setFieldValue, values.inRedFlag]);

  const handleDefectTypeChange = useCallback((value) => {
    // Backend integration: Apply default values based on defect type (exactly like old form)
    const data = setDefaultValues(value);
    setFieldValue('defectType', data.defectType);
    setFieldValue('riskType', data.risk);
    setFieldValue('priority', data.priority);
    setFieldValue('priorityType', data.priority); // Sync both priority fields
    setFieldValue('inRedFlag', data.isRedFlag);
  }, [setFieldValue]);

  const handlePriorityChange = useCallback((value) => {
    // Exactly like old form priority change
    setFieldValue('priority', value);
    setFieldValue('priorityType', value); // Sync both fields
    
    if (value !== null) {
      if (value === 'Multiple') {
        setFieldValue('isMultiple', true);
        setShowPriorityDistribution(true);
        
        // Set default distribution percentages like old form
        const distribution = { 
          shortTerm: 30, 
          mediumTerm: 40, 
          longTerm: 30 
        };
        setFieldValue('percentageShortTermPriority', distribution.shortTerm);
        setFieldValue('percentageMidTermPriority', distribution.mediumTerm);
        setFieldValue('percentageLongTermPriority', distribution.longTerm);
        
        // Set multipleCost object for backend compatibility
        setFieldValue('multipleCost', {
          short: distribution.shortTerm / 100,
          medium: distribution.mediumTerm / 100,
          long: distribution.longTerm / 100
        });
      } else {
        setFieldValue('isMultiple', false);
        setShowPriorityDistribution(false);
        // Clear distribution fields
        setFieldValue('percentageShortTermPriority', 0);
        setFieldValue('percentageMidTermPriority', 0);
        setFieldValue('percentageLongTermPriority', 0);
        setFieldValue('multipleCost', {
          short: 0,
          medium: 0,
          long: 0
        });
      }
    }
  }, [setFieldValue]); // Simplified dependencies

  const handleRedFlagChange = useCallback((checked) => {
    setFieldValue('inRedFlag', checked);
    if (checked) {
      setFieldValue('riskType', 'High');
    }
  }, [setFieldValue]);

  // Handler for priority distribution changes - maps to API field names - optimized
  const handlePriorityDistributionChange = useCallback((field, value) => {
    const numValue = parseInt(value) || 0;
    
    // Update the priorityDistribution object - simplified
    const currentDistribution = {
      shortTerm: 30,
      mediumTerm: 40,
      longTerm: 30
    };
    
    setFieldValue('priorityDistribution', {
      ...currentDistribution,
      [field]: numValue
    });
    
    // Map to individual API fields
    if (field === 'shortTerm') {
      setFieldValue('percentageShortTermPriority', numValue);
    } else if (field === 'mediumTerm') {
      setFieldValue('percentageMidTermPriority', numValue);
    } else if (field === 'longTerm') {
      setFieldValue('percentageLongTermPriority', numValue);
    }
    
    // Update multipleCost for backend compatibility
    const updatedDistribution = { ...currentDistribution, [field]: numValue };
    setFieldValue('multipleCost', {
      short: updatedDistribution.shortTerm / 100,
      medium: updatedDistribution.mediumTerm / 100,
      long: updatedDistribution.longTerm / 100
    });
  }, [setFieldValue]); // Simplified dependencies

  const elementOptions = getAllElementOptions();

  // Helper function to process elements suggestion list (like original)
  const elementsSuggestionList = useCallback((arr, obj, parent) => {
    let temp = arr.reduce((list, curr) => {
      if (
        !list.find((f) => f.language === curr.language && f.value === curr.value)
      ) {
        list.push({
          ...curr,
          parentIdent: obj.subCategorySecondaryIdent,
          parentName: parent.title,
        });
      }
      return list;
    }, []);
    return temp;
  }, []);

  // Process elements suggestion list similar to original ElementSection
  const processElementsSuggestions = useCallback((obj) => {
    // Try to get name in project language first, then fallback
    const parentName = obj.subCategorySecondaryName.find(
      (f) => f.language === scope.projectLanguageCode
    ) || obj.subCategorySecondaryName.find(
      (f) => f.language === 'en'
    ) || obj.subCategorySecondaryName[0];
    
    let temp = [];
    // Get elements from elementDtoList
    obj.elementDtoList?.forEach((item) => {
      if (item.elementName.length) {
        temp = [...temp, ...item.elementName];
      }
    });
    
    // Add suggestions from elementsSuggestionList
    let defaultElementList = [...temp, ...obj.elementsSuggestionList];
    
    // Add "Others" using the standard array from common dropdowns (like original line 87)
    temp = [...defaultElementList, ...others];
    
    // Process elements through the elementsSuggestionList helper (like original line 98)
    const processedElementsList = elementsSuggestionList(temp, obj, parentName);
    
    // Filter to show only the project language version and map to dropdown format
    const processedElements = processedElementsList.filter(element => {
      // For elements with language property, only include project language
      if (element.language && element.language !== scope.projectLanguageCode) {
        // Check if there's a version in the project language
        const hasProjectLanguageVersion = processedElementsList.some(e => 
          e.value === element.value && e.language === scope.projectLanguageCode
        );
        // If project language version exists, exclude this one
        if (hasProjectLanguageVersion) return false;
      }
      
      return true;
    }).map(element => ({
      label: element.title || element.value,
      value: element.value,
      parentIdent: element.parentIdent,
      isSubElement: true
    }));
    
    return {
      name: parentName?.title || parentName?.value || 'Unknown',
      ident: obj.subCategorySecondaryIdent,
      elementsList: processedElements
    };
  }, [scope.projectLanguageCode, elementsSuggestionList]);

  // Effect to initialize isElementOther state based on current form values (backend compatible)
  useEffect(() => {
    // Handle elementType vs elementName field mapping (Enhanced vs Original form)
    let currentElementTypeValue = '';
    
    // Priority: elementType (Enhanced form) -> elementName (Original form)
    if (values.elementType) {
      if (typeof values.elementType === 'object' && values.elementType !== null) {
        const stringValue = values.elementType.value || values.elementType.title || values.elementType.label || '';
        console.log('Converting object elementType to string:', {
          original: values.elementType,
          converted: stringValue
        });
        setFieldValue('elementType', stringValue);
        currentElementTypeValue = stringValue;
      } else if (typeof values.elementType === 'string') {
        currentElementTypeValue = values.elementType;
      }
    } else if (values.elementName) {
      // Handle original form structure where element is stored in elementName
      if (typeof values.elementName === 'object' && values.elementName !== null) {
        const stringValue = values.elementName.value || values.elementName.title || values.elementName.label || '';
        // Debug logging temporarily removed to avoid React rendering errors
        // Set both fields for compatibility
        setFieldValue('elementType', stringValue);
        currentElementTypeValue = stringValue;
      } else if (typeof values.elementName === 'string') {
        // Debug logging temporarily removed
        setFieldValue('elementType', values.elementName);
        currentElementTypeValue = values.elementName;
      }
    }
    
    const isOthers = currentElementTypeValue === 'Sonstiges' || currentElementTypeValue === 'Others';
    
    // Only update if value actually changed to prevent unnecessary re-renders
    if (isOthers !== isElementOther) {
      setIsElementOther(isOthers);
    }
    
    // Backend compatibility: Ensure isElementOther field is set only if changed
    if (isOthers !== values.isElementOther) {
      setFieldValue('isElementOther', isOthers);
    }
  }, [values.elementType, values.elementName, isElementOther, values.isElementOther, setFieldValue]); // Add elementName dependency

  // Effect to initialize subCategory information in form values for API compatibility
  useEffect(() => {
    if (subCategory && !values.subCategory) {
      setFieldValue('subCategory', subCategory);
    }
  }, [subCategory, values.subCategory, setFieldValue]);

  // Effect to fetch element data based on subcategory - optimized to prevent unnecessary calls
  useEffect(() => {
    if (subCategory?.ident && scope.projectIdent) {
      dispatch(
        fetchSubCategoryElementList({
          projectIdent: scope.projectIdent,
          ident: subCategory.ident,
        })
      );
    }
  }, [dispatch, scope.projectIdent, subCategory?.ident]); // Only depend on ident, not entire subCategory object

  // Effect to process fetched element data
  useEffect(() => {
    if (
      elementState.suggestionElements.type === 'FETCHED' &&
      elementState.suggestionElements.data
    ) {
      const processedOptions = [];
      
      // Process all categories, following the original ElementSection logic
      const allCategories = elementState.suggestionElements.data.subCategorySecondaryDtoList || [];
      
      // Filter out "Sonstiges"/"Others" categories initially (like original)
      const regularCategories = allCategories.filter((category) => {
        const categoryName = category.subCategorySecondaryName.find(
          (f) => f.language === scope.projectLanguageCode
        ) || category.subCategorySecondaryName.find(
          (f) => f.language === 'en'
        ) || category.subCategorySecondaryName[0];
        
        return categoryName && categoryName.value !== 'Sonstiges' && categoryName.title !== 'Others';
      });

      // Process each regular category
      regularCategories.forEach((category) => {
        const processed = processElementsSuggestions(category);
        
        // Add category header
        processedOptions.push({
          label: processed.name,
          value: processed.name,
          isHeader: true,
          disabled: true
        });
        
        // Add all elements for this category (regular elements + Others)
        // Note: processElementsSuggestions already includes "Others" like in the original
        processed.elementsList.forEach(element => {
          processedOptions.push({
            label: `  ${element.label}`, // Indent sub-elements
            value: element.value,
            isHeader: false,
            parentIdent: element.parentIdent
          });
        });
      });

      // If no regular categories found, add "Sonstiges"/"Others" as fallback (like original)
      if (regularCategories.length === 0) {
        const sonstigesCategory = allCategories.find((category) => {
          const categoryName = category.subCategorySecondaryName.find(
            (f) => f.language === scope.projectLanguageCode
          ) || category.subCategorySecondaryName.find(
            (f) => f.language === 'en'
          ) || category.subCategorySecondaryName[0];
          
          return categoryName && (categoryName.value === 'Sonstiges' || categoryName.title === 'Others');
        });

        if (sonstigesCategory) {
          const processed = processElementsSuggestions(sonstigesCategory);
          
          processedOptions.push({
            label: processed.name,
            value: processed.name,
            isHeader: true,
            disabled: true
          });
          
          processed.elementsList.forEach(element => {
            processedOptions.push({
              label: `  ${element.label}`,
              value: element.value,
              isHeader: false,
              parentIdent: element.parentIdent
            });
          });
        }
      }

      setFilteredElementOptions(processedOptions);
      dispatch(resetSliceState());
    }
  }, [elementState.suggestionElements, processElementsSuggestions, dispatch, scope.projectLanguageCode]);

  // Memoize element options to prevent unnecessary re-renders - optimized
  const memoizedElementOptions = useMemo(() => {
    if (filteredElementOptions.length > 0) {
      return filteredElementOptions;
    }
    // Fallback to static options if no dynamic data available
    return elementOptions;
  }, [filteredElementOptions, elementOptions]);

  // Helper function to get element type display label
  const getElementTypeLabel = useCallback((elementType) => {
    if (!elementType) return 'Not selected';
    
    const option = memoizedElementOptions.find(opt => opt.value === elementType);
    return option ? option.label : elementType;
  }, [memoizedElementOptions]);

  // Helper function to safely get element type value from form data
  const getElementTypeValue = useCallback((elementType) => {
    if (!elementType) return '';
    if (typeof elementType === 'string') return elementType;
    if (typeof elementType === 'object' && elementType !== null) {
      return elementType.value || elementType.title || elementType.label || '';
    }
    return String(elementType);
  }, []);

  // File handling
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      url: URL.createObjectURL(file),
      file: file
    }));
    setFieldValue('photos', [...(values.photos || []), ...newPhotos]);
  };

  const removePhoto = (id) => {
    const updatedPhotos = (values.photos || []).filter(photo => photo.id !== id);
    setFieldValue('photos', updatedPhotos);
  };

  // AI Enhancement integration with OpenAI backend endpoint
  const paraphraseText = async (text) => {
    try {
      console.log('Sending request to AI API with text:', text);
      const encodedText = encodeURIComponent(text);
      console.log('Encoded text:', encodedText);
      
      const response = await customAxios.post(`/openai/paraphrase?description=${encodedText}`, {}, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader()
        },
        timeout: 30000 // 30 second timeout for AI processing
      });
      
      console.log('Full API Response:', response);
      console.log('Response data:', response.data);
      console.log('Response status:', response.status);
      
      // Handle different possible response structures
      const paraphrasedText = response.data?.paraphrasedText || 
                             response.data?.result || 
                             response.data?.data || 
                             response.data?.text || 
                             response.data?.content ||
                             response.data?.enhanced ||
                             response.data?.paraphrased ||
                             text; // Fallback to original if no paraphrased text found
      
      console.log('Extracted paraphrased text:', paraphrasedText);
      console.log('Paraphrased text type:', typeof paraphrasedText);
      
      return paraphrasedText;
    } catch (error) {
      console.error('AI Enhancement failed:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Fallback to original text if API fails
      return text;
    }
  };

  // AI Enhancement
  const handleAIParaphrase = async (field) => {
    const text = field === 'defect' ? values.defectDescription : values.measureDescription;
    if (!text || !text.trim()) {
      console.log('No text to paraphrase for field:', field);
      return;
    }
    
    console.log('Starting AI paraphrase for field:', field, 'with text:', text.trim());
    
    if (field === 'defect') {
      setAiParaphrasingDefect(true);
    } else {
      setAiParaphrasingMeasure(true);
    }
    
    try {
      const enhanced = await paraphraseText(text.trim());
      console.log('Original text:', text.trim());
      console.log('Enhanced text received:', enhanced);
      console.log('Enhanced text type:', typeof enhanced);
      console.log('Enhanced text length:', enhanced?.length);
      
      // Force update the field with the enhanced text
      if (field === 'defect') {
        console.log('Updating defectDescription field');
        setFieldValue('defectDescription', enhanced);
        // Force a re-render by logging the current value
        setTimeout(() => {
          console.log('Current defectDescription value after update:', values.defectDescription);
        }, 100);
      } else {
        console.log('Updating measureDescription field');
        setFieldValue('measureDescription', enhanced);
        // Force a re-render by logging the current value
        setTimeout(() => {
          console.log('Current measureDescription value after update:', values.measureDescription);
        }, 100);
      }
    } catch (error) {
      console.error('Error in AI enhancement:', error);
      // Keep original text if error occurs
    } finally {
      if (field === 'defect') {
        setAiParaphrasingDefect(false);
      } else {
        setAiParaphrasingMeasure(false);
      }
    }
  };

  // Helper function to get language-based options (optimized for enhanced form)
  const getLanguageBasedOption = useCallback((option, languageCode = 'en') => {
    if (!option) return '';
    
    // Direct string return
    if (typeof option === 'string') return option;
    
    // Object with language codes
    if (option[languageCode]) return option[languageCode];
    
    // Fallback to English or first available
    return option['en'] || option[Object.keys(option)[0]] || '';
  }, []);

  // Form submission - Remove this function as it will be handled by parent CreateDefect component
  // The form submission is handled by the parent component through Formik

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', borderBottom: 1, borderColor: 'grey.200', py: 1.5, px: 3 }}>
        <Typography variant="h5" fontWeight="bold" color="text.primary">
          Enhanced Defect Form
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Grid container spacing={3}>
          {/* Left Column - Defect Information (Wider when non-cost, split when cost defect) */}
          <Grid item xs={12} md={values.costDefect ? 8 : 12}>
            <Card elevation={2} sx={{ borderRadius: 2, minHeight: '800px', height: 'auto' }}>
              <CardHeader 
                title={
                  <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {getSafeTranslation("_Defect-Information")}
                  </Typography>
                }
                avatar={<Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}><Info /></Avatar>}
                sx={{ pb: 1 }}
              />
              <CardContent sx={{ pt: 1, pb: 2, px: 2 }}>
                <Stack spacing={2.5}>
                  {/* Element Type Section - Aligned with Element Name */}
                  <Box sx={{ pl: 0 }}>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          size="small"
                          options={memoizedElementOptions}
                          freeSolo
                          selectOnFocus
                          clearOnBlur
                          handleHomeEndKeys
                          value={(() => {
                            // Handle both string and object values for elementType
                            // Priority: elementType (Enhanced form) -> elementName.value (Original form)
                            let elementTypeValue = '';
                            
                            if (values.elementType && typeof values.elementType === 'string') {
                              elementTypeValue = values.elementType;
                            } else if (typeof values.elementType === 'object' && values.elementType !== null) {
                              elementTypeValue = values.elementType.value || values.elementType.title || values.elementType.label || '';
                            } else if (values.elementName) {
                              // Check elementName (original form structure)
                              if (typeof values.elementName === 'string') {
                                elementTypeValue = values.elementName;
                              } else if (typeof values.elementName === 'object' && values.elementName !== null) {
                                elementTypeValue = values.elementName.value || values.elementName.title || values.elementName.label || '';
                              }
                            }
                            
                            const foundOption = memoizedElementOptions.find(option => option.value === elementTypeValue);
                            // Debug logging temporarily removed to avoid React rendering errors
                            
                            // If no exact match found, try to find a partial match or create a custom option
                            if (!foundOption && elementTypeValue) {
                              // console.log('No exact match found, creating custom option for:', elementTypeValue);
                              return {
                                label: elementTypeValue,
                                value: elementTypeValue,
                                isCustom: true
                              };
                            }
                            
                            return foundOption || null;
                          })()}
                          onChange={(event, newValue) => {
                            // Debug logging temporarily removed to avoid React rendering errors
                            
                            // Fix: Handle null/undefined newValue properly
                            let selectedValue = '';
                            if (newValue && typeof newValue === 'object' && newValue.value) {
                              selectedValue = newValue.value;
                            } else if (newValue && typeof newValue === 'string') {
                              selectedValue = newValue;
                            }
                            // If newValue is null/undefined, selectedValue stays empty string
                            
                            // Ensure we always set a string value, never an object
                            setFieldValue('elementType', selectedValue);
                            
                            // Check if "Others" or "Sonstiges" is selected
                            const isOthersSelected = selectedValue === 'Sonstiges' || selectedValue === 'Others';
                            setIsElementOther(isOthersSelected);
                            setFieldValue('isElementOther', isOthersSelected);
                            
                            if (isOthersSelected) {
                              // Clear elementName when Others is selected so user can type custom name
                              setFieldValue('elementName', '');
                              // Set customElement when Others is selected (backend compatibility)
                              setFieldValue('customElement', {
                                value: '',
                                title: {
                                  de: '',
                                  en: '',
                                },
                                parentIdent: newValue?.parentIdent || subCategory?.ident || '',
                              });
                            } else if (selectedValue) {
                              // For regular elements, set elementName as object with parentIdent (backend compatibility)
                              setFieldValue('elementName', {
                                value: selectedValue,
                                title: newValue?.label || selectedValue,
                                label: newValue?.label || selectedValue,
                                parentIdent: newValue?.parentIdent || subCategory?.ident || '',
                                language: scope.projectLanguageCode || 'de'
                              });
                              // Clear customElement when regular element is selected
                              setFieldValue('customElement', null);
                              
                              // Debug logging temporarily removed to avoid React rendering errors
                            } else {
                              // Clear all when no selection (element type cleared)
                              setFieldValue('elementName', '');
                              setFieldValue('customElement', null);
                              setIsElementOther(false);
                              setFieldValue('isElementOther', false);
                            }
                          }}
                          getOptionLabel={(option) => {
                            if (typeof option === 'string') return option;
                            return option.label || option.value || '';
                          }}
                          getOptionDisabled={(option) => option.disabled || false}
                          isOptionEqualToValue={(option, value) => {
                            if (typeof option === 'string' && typeof value === 'string') return option === value;
                            if (typeof option === 'object' && typeof value === 'string') return option.value === value;
                            if (typeof option === 'string' && typeof value === 'object') return option === value.value;
                            return option.value === value.value;
                          }}
                          filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;
                            
                            // Simple search - just filter by label text, keep original structure
                            return options.filter(option => {
                              // Always include headers for structure
                              if (option.isHeader) return true;
                              // Filter sub-elements by search text
                              return option.label.toLowerCase().includes(inputValue.toLowerCase());
                            });
                          }}
                          renderOption={(props, option) => (
                            <li 
                              {...props} 
                              key={`${option.value}-${option.isHeader ? 'header' : 'option'}`}
                              style={{
                                fontWeight: option.isHeader ? 'bold' : 'normal',
                                backgroundColor: option.isHeader ? '#f5f5f5' : 'white',
                                paddingLeft: option.isHeader ? 8 : 24,
                                cursor: option.isHeader ? 'default' : 'pointer',
                                pointerEvents: option.isHeader ? 'none' : 'auto'
                              }}
                            >
                              {option.label}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Element Type *"
                              variant="outlined"
                              size="small"
                              placeholder="Select Element Type"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  height: '40px',
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#1976d2',
                                  },
                                },
                              }}
                              error={touched.elementType && errors.elementType}
                              helperText={touched.elementType && errors.elementType}
                            />
                          )}
                          sx={{ width: '100%' }}
                          blurOnSelect
                          noOptionsText="No matching elements found"
                          openOnFocus
                        />
                      </Grid>

                      {/* Element Name field - Shows when "Others" is selected, positioned on the right */}
                      {isElementOther && (
                        <Grid item xs={12} md={6}>
                          <TextField
                            size="small"
                            fullWidth
                            label={getSafeTranslation("_Element-Name") + " *"}
                            value={(() => {
                              // Handle both object and string values for elementName
                              if (typeof values.elementName === 'object' && values.elementName !== null) {
                                return values.elementName.value || values.elementName.title || '';
                              }
                              return safeStringValue(values.elementName);
                            })()}
                            onChange={(e) => {
                              setFieldValue('elementName', e.target.value);
                            }}
                            placeholder={getSafeTranslation("_Element-Name")}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label="clear element name"
                                    onClick={() => {
                                      setFieldValue('elementName', '');
                                    }}
                                    edge="end"
                                    size="small"
                                    sx={{ 
                                      visibility: values.elementName ? 'visible' : 'hidden'
                                    }}
                                  >
                                    <X fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '40px',
                              },
                            }}
                            error={touched.elementName && !!errors.elementName}
                            helperText={touched.elementName && errors.elementName ? (typeof errors.elementName === 'string' ? errors.elementName : 'Please enter a valid element name') : ''}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  {/* Defects Database Search Integration - Positioned after Element Type */}
                  <DefectDatabaseSearch
                    setFieldValue={setFieldValue}
                    elementName={(() => {
                      if (isElementOther) {
                        return typeof values.elementName === 'object' && values.elementName !== null 
                          ? (values.elementName.value || values.elementName.title || '') 
                          : (values.elementName || '');
                      } else {
                        return typeof values.elementType === 'object' && values.elementType !== null 
                          ? (values.elementType.value || values.elementType.title || '') 
                          : (values.elementType || '');
                      }
                    })()}
                    disabled={false} // Always show the search component
                    onDefectSelected={(defect) => {
                      // DefectDatabaseSearch already calls applyDefectDatabaseToForm with proper mapping
                      // No need to manually set fields here as it's already handled
                      // Debug logging temporarily removed
                    }}
                  />

                  {/* Radio Button Groups - After element fields */}
                  <Box sx={{ mt: 1 }}>
                    {/* Risk Level */}
                    <Box sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <FormLabel component="div" sx={{ color: '#00000099', fontSize: '0.875rem', fontWeight: 'normal' }}>
                          {getSafeTranslation('_Choose-Risk-Type')}
                        </FormLabel>
                        <StyledTooltip title={getTranslationVariantText("Please Hover on Risks", "subtitile")} placement="top" arrow>
                          <IconButton size="small" sx={{ ml: 0.5, p: 0.5 }}>
                            <Info fontSize="small" />
                          </IconButton>
                        </StyledTooltip>
                      </Box>
                      <RadioGroup row
                        value={safeStringValue(values.riskType)}
                        onChange={(e) => handleRiskChange(e.target.value)}
                        sx={{ gap: 0.25 }}
                      >
                        {riskTypeOptionsArray.map((risk) => {
                          const warningType =
                            risk.value === "niedrig"
                              ? "low"
                              : risk.value === "mittel"
                              ? "medium"
                              : "high";

                          return (
                            <StyledTooltip
                              key={risk.value}
                              title={<WarningContent type={warningType} />}
                              placement="top"
                              arrow
                            >
                              <FormControlLabel
                                value={risk.value}
                                control={<Radio sx={radioButton} name="riskType" size="small" />}
                                label={
                                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                                    {getLanguageBasedOption(risk, scope.projectLanguageCode)}
                                  </Typography>
                                }
                                labelPlacement="end"
                              />
                            </StyledTooltip>
                          );
                        })}
                      </RadioGroup>
                    </Box>

                    {/* Defect Type */}
                    <Box sx={{ mb: 1.5 }}>
                      <FormLabel
                        component="div"
                        sx={{
                          mb: 1,
                          color: touched.defectType && errors.defectType ? "#f44336" : "#00000099",
                          fontWeight: 'normal',
                          fontSize: '0.875rem'
                        }}
                        error={touched.defectType && errors.defectType}
                      >
                        {getSafeTranslation('_Choose-a-Defect-Type')}
                        <FormHelperText
                          error={true}
                          variant="standard"
                          sx={{ color: '#f44336' }}
                        >
                          {touched.defectType && errors.defectType}
                        </FormHelperText>
                      </FormLabel>
                      <RadioGroup
                        row
                        value={safeStringValue(values.defectType)}
                        onChange={(e) => handleDefectTypeChange(e.target.value)}
                        sx={{ gap: 0.25, flexWrap: 'wrap' }}
                      >
                        {defectTypeOptions.map((type) => (
                          <FormControlLabel
                            key={type.value}
                            value={type.value}
                            control={<Radio sx={radioButton} name="defectType" size="small" />}
                            label={
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                {getLanguageBasedOption(type, scope.projectLanguageCode)}
                              </Typography>
                            }
                            labelPlacement="end"
                          />
                        ))}
                      </RadioGroup>
                    </Box>

                    {/* Priority Type */}
                    <Box sx={{ mb: 1 }}>
                      <FormLabel
                        component="div"
                        sx={{
                          mb: 1,
                          color: touched.priority && errors.priority ? "#f44336" : "#00000099",
                          fontWeight: 'normal',
                          fontSize: '0.875rem'
                        }}
                        error={touched.priority && errors.priority}
                      >
                        {getSafeTranslation('_Choose-a-Priority-Type')}
                        <FormHelperText
                          error={true}
                          variant="standard"
                          sx={{ color: '#f44336' }}
                        >
                          {touched.priority && errors.priority}
                        </FormHelperText>
                      </FormLabel>
                      <RadioGroup
                        row
                        value={safeStringValue(values.priority)}
                        onChange={(e) => handlePriorityChange(e.target.value)}
                        sx={{ gap: 0.25 }}
                      >
                        {priorityTypeOptions.map((priority) => (
                          <FormControlLabel
                            key={priority.value}
                            value={priority.value}
                            control={<Radio sx={radioButton} name="priority" size="small" />}
                            label={
                              <Typography variant="body2" sx={{ ml: 0.5 }}>
                                {getLanguageBasedOption(priority, scope.projectLanguageCode)}
                              </Typography>
                            }
                            labelPlacement="end"
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                  </Box>

                  {/* Main Description Fields - Aligned with radio groups */}
                  <Box sx={{ pl: 0 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          multiline
                          rows={8}
                          size="small"
                          label="Defect Description *"
                          value={safeStringValue(values.defectDescription)}
                          onChange={(e) => setFieldValue('defectDescription', e.target.value)}
                          placeholder="Describe the defect in detail..."
                          error={touched.defectDescription && errors.defectDescription}
                          helperText={touched.defectDescription && errors.defectDescription}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => handleAIParaphrase('defect')}
                                  disabled={aiParaphrasingDefect || !values.defectDescription}
                                  size="small"
                                >
                                  {aiParaphrasingDefect ? <CircularProgress size={16} /> : <Wand2 />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fafafa',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                              },
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          multiline
                          rows={8}
                          size="small"
                          label="Corrective Measure Description *"
                          value={safeStringValue(values.measureDescription)}
                          onChange={(e) => setFieldValue('measureDescription', e.target.value)}
                          placeholder="Describe the corrective measures..."
                          error={touched.measureDescription && errors.measureDescription}
                          helperText={touched.measureDescription && errors.measureDescription}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => handleAIParaphrase('measure')}
                                  disabled={aiParaphrasingMeasure || !values.measureDescription}
                                  size="small"
                                >
                                  {aiParaphrasingMeasure ? <CircularProgress size={16} /> : <Wand2 />}
                                </IconButton>
                              </InputAdornment>
                            )
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: '#fafafa',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                              },
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    {/* Additional Information Fields - With better spacing */}
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', fontWeight: 500 }}>
                        {getSafeTranslation("_Additional-Information")}
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            label="Quelle"
                            value={safeStringValue(values.quelle)}
                            onChange={(e) => setFieldValue('quelle', e.target.value)}
                            placeholder="Source information..."
                            error={touched.quelle && errors.quelle}
                            helperText={touched.quelle && errors.quelle}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            label={getTextLabel("_Data-Room-Index")}
                            value={safeStringValue(values.dataRoomIndex)}
                            onChange={(e) => setFieldValue('dataRoomIndex', e.target.value)}
                            placeholder="Data room index..."
                            error={touched.dataRoomIndex && errors.dataRoomIndex}
                            helperText={touched.dataRoomIndex && errors.dataRoomIndex}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            label={getTextLabel("_Q&A")}
                            value={safeStringValue(values.qaField || values.questionAndAnswer)}
                            onChange={(e) => {
                              const value = e.target.value;
                              setFieldValue('qaField', value);
                              setFieldValue('questionAndAnswer', value); // Backend compatibility
                            }}
                            placeholder="Q&A information..."
                            error={touched.qaField && errors.qaField}
                            helperText={touched.qaField && errors.qaField}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  </Box>



                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Cost Analysis (Only show when cost defect) */}
          {values.costDefect && (
            <Grid item xs={12} md={4}>
              <Card elevation={2} sx={{ borderRadius: 2, height: 'fit-content', position: 'sticky', top: 20 }}>
                <CardHeader 
                  title={
                    <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                      {getSafeTranslation("_Cost-Data")}
                    </Typography>
                  }
                  avatar={<Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}><DollarSign /></Avatar>}
                  sx={{ pb: 1 }}
                />
                <CardContent sx={{ pt: 1, px: 1.5 }}>
                  <Stack spacing={2}>
                    <Grid container spacing={1.5} sx={{ px: 0.5 }}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={getTextLabel("_Price-per-Unit")}
                          type="number"
                          value={safeStringValue(values.pricePerUnit)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('pricePerUnit', value ? Number(value) : null);
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              textAlign: 'center'
                            }
                          }}
                          error={touched.pricePerUnit && errors.pricePerUnit}
                          helperText={touched.pricePerUnit && errors.pricePerUnit}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth size="small" error={touched.unit && errors.unit}>
                          <InputLabel>{getTextLabel("_Unit")}</InputLabel>
                          <Select
                            value={safeStringValue(values.unit)}
                            label={getTextLabel("_Unit")}
                            onChange={(e) => setFieldValue('unit', e.target.value)}
                            sx={{
                              '& .MuiSelect-select': {
                                textAlign: 'center'
                              }
                            }}
                          >
                            {getUnits().map((unit) => (
                              <MenuItem key={unit.value} value={unit.value}>
                                {unit.title}
                              </MenuItem>
                            ))}
                          </Select>
                          {touched.unit && errors.unit && (
                            <FormHelperText>{errors.unit}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={getTextLabel("_Quantity")}
                          type="number"
                          value={safeStringValue(values.quantity)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('quantity', value ? Number(value) : null);
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              textAlign: 'center'
                            }
                          }}
                          error={touched.quantity && errors.quantity}
                          helperText={touched.quantity && errors.quantity}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          size="small"
                          label={getTextLabel("_Extra-Cost-(Optional)")}
                          type="number"
                          value={safeStringValue(values.extraCost)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('extraCost', value ? Number(value) : null);
                          }}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">€</InputAdornment>
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              textAlign: 'center'
                            }
                          }}
                          error={touched.extraCost && errors.extraCost}
                          helperText={touched.extraCost && errors.extraCost}
                        />
                      </Grid>
                      {/* Move % of element affected to a separate row */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label={getTextLabel("_%-of-the-element-affected")}
                          type="number"
                          value={safeStringValue(values.elementAffected)}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFieldValue('elementAffected', value ? Number(value) : null);
                          }}
                          InputProps={{ 
                            inputProps: { min: 1, max: 100 },
                            endAdornment: <InputAdornment position="end">%</InputAdornment>
                          }}
                          placeholder="Min: 1, Max: 100"
                          sx={{
                            '& .MuiInputBase-input': {
                              textAlign: 'center'
                            }
                          }}
                          error={touched.elementAffected && errors.elementAffected}
                          helperText={touched.elementAffected && errors.elementAffected}
                        />
                      </Grid>
                    </Grid>
                    
                    <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, mx: 0.5 }}>
                      <Typography variant="h6" fontWeight="bold" color="success.dark">
                        Total Cost: €{calulateTotal(values, false) || 0}
                      </Typography>
                    </Box>

                    {/* Cost Threshold Warning - Same as old form */}
                    {values.pricePerUnit > 0 &&
                      Math.round(convertToDecimal(values, true) / 1000) * 1000 < 10000 && (
                        <Box sx={{ mx: 0.5, mt: 1 }}>
                          <Alert variant="filled" severity="warning">
                            {getIntlTranslation("_threshold")}
                          </Alert>
                        </Box>
                      )}

                    <Box sx={{ px: 0.5, mt: 2 }}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        size="small"
                        label={getLabel("_Extra-Cost-Comments")}
                        value={safeStringValue(values.extraCostComment || values.costComment)}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Backend integration: Map to both field names for compatibility
                          setFieldValue('costComment', value);
                          setFieldValue('extraCostComment', value);
                        }}
                        placeholder="Internal comments..."
                        sx={{
                          '& .MuiInputBase-input': {
                            textAlign: 'left'
                          }
                        }}
                        error={touched.extraCostComment && errors.extraCostComment}
                        helperText={touched.extraCostComment && errors.extraCostComment}
                      />
                    </Box>

                    {/* Priority Distribution Table - Show when Multiple priority is selected and it's a cost defect */}
                    {showPriorityDistribution && values.costDefect && (
                      <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 'bold', color: 'warning.dark', textAlign: 'center' }}>
                          Priority Distribution
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Short Term (%)"
                              type="number"
                              value={safeStringValue(values.percentageShortTermPriority) || 30}
                              onChange={(e) => handlePriorityDistributionChange('shortTerm', e.target.value)}
                              InputProps={{ inputProps: { min: 0, max: 100 } }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  textAlign: 'center'
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Medium Term (%)"
                              type="number"
                              value={safeStringValue(values.percentageMidTermPriority) || 40}
                              onChange={(e) => handlePriorityDistributionChange('mediumTerm', e.target.value)}
                              InputProps={{ inputProps: { min: 0, max: 100 } }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  textAlign: 'center'
                                }
                              }}
                            />
                          </Grid>
                          <Grid item xs={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Long Term (%)"
                              type="number"
                              value={safeStringValue(values.percentageLongTermPriority) || 30}
                              onChange={(e) => handlePriorityDistributionChange('longTerm', e.target.value)}
                              InputProps={{ inputProps: { min: 0, max: 100 } }}
                              sx={{
                                '& .MuiInputBase-input': {
                                  textAlign: 'center'
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Total should equal 100%. Distribution affects cost calculations across time periods.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Photo Documentation - Always visible */}
        <Box sx={{ mt: 3 }}>
          <PhotoDocumentationSection
            formData={{
              ...values,
              photos: values.photos || []
            }}
            fileInputRef={fileInputRef}
            handleFileSelect={handleFileSelect}
            removePhoto={removePhoto}
            uploading={false}
          />
        </Box>
      </Container>

      {/* Sticky Bottom Form Summary */}
      {(values.elementType || values.riskType || values.priorityType || values.priority || values.defectType) && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            bgcolor: 'white',
            borderTop: 1,
            borderColor: 'grey.300',
            py: 1,
            px: 3,
            zIndex: 1000,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
          }}
        >
          <Container maxWidth="xl">
            <Stack direction="row" spacing={3} alignItems="center" sx={{ minHeight: 28 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                <Typography component="span" sx={{ fontWeight: 'bold' }}>Element:</Typography>{' '}
                {isElementOther 
                  ? (values.elementName || 'Enter custom name')
                  : getElementTypeLabel(values.elementType)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                <Typography component="span" sx={{ fontWeight: 'bold' }}>Risk:</Typography>{' '}
                {safeStringValue(values.riskType) || 'Not selected'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                <Typography component="span" sx={{ fontWeight: 'bold' }}>Priority:</Typography>{' '}
                {safeStringValue(values.priorityType || values.priority) || 'Not selected'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                <Typography component="span" sx={{ fontWeight: 'bold' }}>Type:</Typography>{' '}
                {safeStringValue(values.defectType) || 'Not selected'}
              </Typography>
              {values.costDefect && (
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  <Typography component="span" sx={{ fontWeight: 'bold' }}>Cost:</Typography>{' '}
                  €{calulateTotal(values, false) || 0}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                <Typography component="span" sx={{ fontWeight: 'bold' }}>Photos:</Typography>{' '}
                {values.photos?.length || 0}
              </Typography>
            </Stack>
          </Container>
        </Box>
      )}
    </Box>
  );
}

export default EnhancedDefectForm;
